import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import {
  X,
  CreditCard,
  Lock,
  Calendar,
  UserCheck,
  Store,
  Tag,
  Building,
  User,
  DollarSign,
  Plus,
  PieChart
} from 'lucide-react';
import { showToast } from '@/lib/custom-toast';
import { getTodayLocalDateString } from '@/utils/dateUtils';
import { formatCurrencyInput, parseCurrency, formatCurrency } from '@/utils/currencyFormat';

const QUICK_MSI_OPTIONS = [3, 6, 9, 12, 18, 24];

const CreditCardBreakdownModal = ({
  isOpen,
  onClose,
  onSuccess,
  account, // Cuenta de tarjeta de crédito actual
  initialMode = 'normal', // 'normal' | 'msi' | 'retention'
  initialEntryType = 'expense', // 'expense' (Nueva Compra) | 'breakdown' (Desglosar Saldo)
  editingTransaction = null
}) => {
  const { token, user } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Tipo de Registro: 'expense' (Nueva Compra, aumenta deuda) | 'breakdown' (Desglosar Saldo existente)
  const [entryType, setEntryType] = useState(initialEntryType || 'expense');

  // Nueva Categoría Modal
  const [showNewCatModal, setShowNewCatModal] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [creatingCat, setCreatingCat] = useState(false);

  // 1. Datos Generales de la Compra
  const [merchant, setMerchant] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getTodayLocalDateString());
  const [categoryId, setCategoryId] = useState('');

  // 2. Destino de la Compra: 'personal' o 'shared' (de alguien más)
  const [ownership, setOwnership] = useState('personal'); // 'personal' | 'shared'

  // 3. Modalidad para Compra Personal ('normal' | 'msi' | 'retention')
  const [personalMode, setPersonalMode] = useState(initialMode || 'normal');
  const [personalMsiMonths, setPersonalMsiMonths] = useState(12);
  const [isCustomPersonalMsi, setIsCustomPersonalMsi] = useState(false);
  const [customPersonalMsiMonths, setCustomPersonalMsiMonths] = useState('');
  const [personalMsiMonthsPaid, setPersonalMsiMonthsPaid] = useState(0);
  const [personalRetentionDate, setPersonalRetentionDate] = useState('');
  const [personalRetentionAutoReminder, setPersonalRetentionAutoReminder] = useState(true);

  // 4. Modalidad y Datos cuando es "De Alguien Más"
  const [sharedName, setSharedName] = useState('');
  const [sharedEmail, setSharedEmail] = useState('');
  const [sharedPaymentMode, setSharedPaymentMode] = useState('full'); // 'full' | 'installments' | 'retention'
  const [sharedInstallments, setSharedInstallments] = useState(12);
  const [isCustomSharedInstallments, setIsCustomSharedInstallments] = useState(false);
  const [customSharedInstallments, setCustomSharedInstallments] = useState('');
  const [sharedRetentionDate, setSharedRetentionDate] = useState('');
  const [sharedRetentionAutoReminder, setSharedRetentionAutoReminder] = useState(true);
  const [sharedDueDate, setSharedDueDate] = useState('');
  const [sharedAutoReminder, setSharedAutoReminder] = useState(true);

  // Datos Bancarios (SPEI) para recibir el dinero del deudor
  const [bankHolder, setBankHolder] = useState(() => {
    try { return localStorage.getItem('saved_bank_holder') || user?.name || ''; } catch { return ''; }
  });
  const [bankName, setBankName] = useState(() => {
    try { return localStorage.getItem('saved_bank_name') || ''; } catch { return ''; }
  });
  const [bankClabe, setBankClabe] = useState(() => {
    try { return localStorage.getItem('saved_bank_clabe') || ''; } catch { return ''; }
  });

  // Touch Drag-Down State (Mobile bottom-sheet)
  const [dragStartY, setDragStartY] = useState(0);
  const [dragCurrentY, setDragCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Lock Body Scroll when Open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset or prefill form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editingTransaction) {
        // Modo Edición: Prellenar con los datos existentes
        setEntryType(editingTransaction.is_breakdown ? 'breakdown' : 'expense');
        setMerchant(editingTransaction.merchant || (account?.credit_card_type === 'department' ? account?.name || '' : ''));
        setDescription(editingTransaction.description || '');
        setAmount(editingTransaction.amount ? String(editingTransaction.amount) : '');
        setDate(editingTransaction.date ? editingTransaction.date.split('T')[0] : getTodayLocalDateString());
        setCategoryId(editingTransaction.category_id || '');

        const isSharedTx = Boolean(editingTransaction.shared_with_name || editingTransaction.shared_amount);
        if (isSharedTx) {
          setOwnership('shared');
          setSharedName(editingTransaction.shared_with_name || '');
          setSharedEmail(editingTransaction.shared_email || '');
          const payMode = editingTransaction.is_retention
            ? 'retention'
            : (editingTransaction.is_msi ? 'installments' : 'full');
          setSharedPaymentMode(payMode);
          const inst = editingTransaction.msi_months || editingTransaction.shared_installments || 12;
          setSharedInstallments(inst);
          setSharedRetentionDate(editingTransaction.retention_expected_release_date || '');
          setSharedRetentionAutoReminder(editingTransaction.retention_auto_reminder ?? true);
          setSharedDueDate(editingTransaction.shared_due_date || '');
          setSharedAutoReminder(editingTransaction.shared_auto_reminder ?? true);
          if (editingTransaction.bank_holder) setBankHolder(editingTransaction.bank_holder);
          if (editingTransaction.bank_name) setBankName(editingTransaction.bank_name);
          if (editingTransaction.bank_clabe) setBankClabe(editingTransaction.bank_clabe);
        } else {
          setOwnership('personal');
          const pMode = editingTransaction.is_retention
            ? 'retention'
            : (editingTransaction.is_msi ? 'msi' : 'normal');
          setPersonalMode(pMode);
          const msiM = editingTransaction.msi_months || 12;
          setPersonalMsiMonths(msiM);
          setPersonalMsiMonthsPaid(editingTransaction.msi_months_paid || 0);
          setPersonalRetentionDate(editingTransaction.retention_expected_release_date || '');
          setPersonalRetentionAutoReminder(editingTransaction.retention_auto_reminder ?? true);
        }
      } else {
        // Modo Creación Normal
        setEntryType(initialEntryType || 'breakdown');
        const isDept = account?.credit_card_type === 'department';
        setMerchant(isDept ? account?.name || '' : '');
        setDescription('');
        setAmount('');
        const today = getTodayLocalDateString();
        setDate(today);
        setOwnership('personal');
        setPersonalMode(initialMode || 'normal');
        setPersonalMsiMonths(12);
        setIsCustomPersonalMsi(false);
        setCustomPersonalMsiMonths('');
        setPersonalMsiMonthsPaid(0);
        setPersonalRetentionDate('');
        setPersonalRetentionAutoReminder(true);

        setSharedName('');
        setSharedEmail('');
        setSharedPaymentMode(initialMode === 'msi' ? 'installments' : (initialMode === 'retention' ? 'retention' : 'full'));
        setSharedInstallments(12);
        setSharedRetentionDate('');
        setSharedRetentionAutoReminder(true);
        setSharedDueDate('');
        setSharedAutoReminder(true);
      }

      fetchCategories();
      fetchBankAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, account, initialMode, initialEntryType, editingTransaction]);

  // Fetch todas las categorías disponibles (sin descartar personalizadas ni generales)
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allCats = res.data || [];
      // Filtramos solo si es explícitamente income, para gastos dejamos todas
      const expenseCats = allCats.filter(c => c.type !== 'income');
      const finalCats = expenseCats.length > 0 ? expenseCats : allCats;
      setCategories(finalCats);
      if (finalCats.length > 0 && !categoryId) {
        setCategoryId(finalCats[0].id);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Fetch cuentas bancarias para precargar datos SPEI
  const fetchBankAccounts = async () => {
    try {
      const res = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const banks = (res.data || []).filter(a => a.type === 'bank' || a.type === 'digital_wallet');
      setBankAccounts(banks);
      if (banks.length > 0 && !bankName) {
        setBankName(banks[0].name);
      }
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
    }
  };

  // Auto-calcular meses ya pagados si la compra fue pasada
  useEffect(() => {
    if (!date) return;
    try {
      const purchaseDate = new Date(date + 'T12:00:00');
      const now = new Date();
      const monthsDiff = (now.getFullYear() - purchaseDate.getFullYear()) * 12 + (now.getMonth() - purchaseDate.getMonth());
      if (monthsDiff > 0) {
        setPersonalMsiMonthsPaid(monthsDiff);
      } else {
        setPersonalMsiMonthsPaid(0);
      }
    } catch {
      // ignore
    }
  }, [date]);

  const parsedAmount = parseFloat(amount) || 0;

  // Cálculos Personal MSI
  const activePersonalMsiMonths = isCustomPersonalMsi ? parseInt(customPersonalMsiMonths) || 12 : parseInt(personalMsiMonths) || 12;
  const personalMonthlyPayment = activePersonalMsiMonths > 0 ? (parsedAmount / activePersonalMsiMonths) : 0;
  const remainingPersonalMsiMonths = Math.max(0, activePersonalMsiMonths - personalMsiMonthsPaid);
  const remainingPersonalMsiBalance = Math.max(0, parsedAmount - (personalMsiMonthsPaid * personalMonthlyPayment));

  // Cálculos De Alguien Más MSI
  const activeSharedInstallments = isCustomSharedInstallments ? parseInt(customSharedInstallments) || 12 : parseInt(sharedInstallments) || 12;
  const sharedMonthlyPayment = activeSharedInstallments > 0 ? (parsedAmount / activeSharedInstallments) : 0;

  // Crear Categoría Rápida
  const handleCreateCategory = async (e) => {
    e && e.preventDefault();
    if (!newCatName.trim()) {
      showToast.error('Escribe el nombre de la categoría');
      return;
    }
    setCreatingCat(true);
    try {
      const res = await axios.post(`${API}/categories`, {
        name: newCatName.trim(),
        type: 'expense',
        icon: 'Tag',
        color: '#6366F1'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const created = res.data;
      setCategories(prev => [...prev, created]);
      setCategoryId(created.id);
      setNewCatName('');
      setShowNewCatModal(false);
      showToast.success(`Categoría "${created.name}" creada`);
    } catch (err) {
      console.error('Error al crear categoría:', err);
      showToast.error(err?.response?.data?.detail || 'No se pudo crear la categoría');
    } finally {
      setCreatingCat(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      showToast.error('Ingresa el concepto de la compra');
      return;
    }
    if (parsedAmount <= 0) {
      showToast.error('Ingresa el precio de la compra');
      return;
    }

    const isShared = ownership === 'shared';

    if (isShared && !sharedName.trim()) {
      showToast.error('Ingresa el nombre de quién te debe');
      return;
    }

    // Persistir datos bancarios para futuras compras
    if (isShared) {
      try {
        if (bankHolder) localStorage.setItem('saved_bank_holder', bankHolder);
        if (bankName) localStorage.setItem('saved_bank_name', bankName);
        if (bankClabe) localStorage.setItem('saved_bank_clabe', bankClabe);
      } catch {}
    }

    // Determinar MSI y Retención según si es personal o de alguien más
    const isMsi = isShared ? (sharedPaymentMode === 'installments') : (personalMode === 'msi');
    const isRetention = isShared ? (sharedPaymentMode === 'retention') : (personalMode === 'retention');
    const activeMsiM = isShared ? activeSharedInstallments : activePersonalMsiMonths;
    const activeMsiPaid = isShared ? 0 : parseInt(personalMsiMonthsPaid) || 0;
    const activeMsiMonthly = isShared ? sharedMonthlyPayment : personalMonthlyPayment;

    const activeRetentionDate = isShared ? sharedRetentionDate : personalRetentionDate;
    const activeRetentionReminder = isShared ? sharedRetentionAutoReminder : personalRetentionAutoReminder;

    setLoading(true);
    try {
      const payload = {
        amount: parsedAmount,
        description: description.trim(),
        merchant: merchant.trim() || null,
        date: date || getTodayLocalDateString(),
        category_id: categoryId || (categories[0]?.id || ''),
        type: 'expense',
        account_id: account?.id || null,
        is_msi: isMsi,
        msi_months: isMsi ? activeMsiM : null,
        msi_months_paid: isMsi ? activeMsiPaid : 0,
        msi_monthly_payment: isMsi ? parseFloat(activeMsiMonthly.toFixed(2)) : null,
        is_retention: isRetention,
        retention_status: isRetention ? 'active' : null,
        retention_expected_release_date: isRetention ? (activeRetentionDate || null) : null,
        retention_auto_reminder: isRetention ? activeRetentionReminder : false,
        // Datos de cobro compartido si pagué por alguien más
        shared_with_name: isShared ? sharedName.trim() : null,
        shared_email: isShared && sharedEmail ? sharedEmail.trim().toLowerCase() : null,
        shared_amount: isShared ? parsedAmount : null,
        shared_due_date: isShared && sharedDueDate ? sharedDueDate : null,
        shared_auto_reminder: isShared && sharedDueDate ? sharedAutoReminder : false,
        bank_holder: isShared ? (bankHolder || user?.name || null) : null,
        bank_name: isShared ? (bankName || null) : null,
        bank_clabe: isShared ? (bankClabe || null) : null,
        shared_payment_mode: isShared ? (sharedPaymentMode === 'installments' ? 'installments' : 'full') : 'full',
        shared_installments: isShared && sharedPaymentMode === 'installments' ? activeSharedInstallments : null,
        shared_installment_amount: isShared && sharedPaymentMode === 'installments' && activeSharedInstallments > 0 ? parseFloat(sharedMonthlyPayment.toFixed(2)) : null,
        // Si es 'breakdown' solo atribuye saldo sin aumentar deuda; si es 'expense' es compra nueva
        is_breakdown: entryType === 'breakdown'
      };

      if (editingTransaction) {
        await axios.put(`${API}/transactions/${editingTransaction.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast.success('Movimiento actualizado');
      } else {
        await axios.post(`${API}/transactions`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast.success(entryType === 'breakdown' ? 'Desglose registrado en la tarjeta' : 'Compra registrada en la tarjeta');
      }

      window.dispatchEvent(new CustomEvent('transaction-added'));
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Error guardando movimiento de tarjeta:', err);
      showToast.error(err?.response?.data?.detail || 'Error al guardar movimiento');
    } finally {
      setLoading(false);
    }
  };

  // Drag down handlers (Mobile)
  const isFromHandle = useRef(false);

  const handleTouchStart = (e, fromHandle = false) => {
    isFromHandle.current = fromHandle;
    setDragStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const diff = e.touches[0].clientY - dragStartY;
    if (diff > 0) {
      setDragCurrentY(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const threshold = isFromHandle.current ? 75 : 190;
    if (dragCurrentY > threshold) {
      onClose();
    }
    setDragCurrentY(0);
    isFromHandle.current = false;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 overflow-x-hidden w-full max-w-full"
      style={{ touchAction: 'pan-y' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Container Modal */}
      <div
        style={{
          transform: isDragging && dragCurrentY > 0 ? `translateY(${dragCurrentY}px)` : undefined,
          transition: isDragging ? 'none' : 'transform 0.2s ease-out',
          touchAction: 'pan-y'
        }}
        className="w-full max-w-full sm:max-w-xl bg-white dark:bg-slate-900 rounded-t-[2.2rem] sm:rounded-3xl shadow-2xl border border-slate-200/80 dark:border-slate-800 max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Drag Bar (Mobile) */}
        <div
          onTouchStart={(e) => handleTouchStart(e, true)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'none' }}
          className="sm:hidden pt-3 pb-2 cursor-grab active:cursor-grabbing flex justify-center shrink-0 select-none"
        >
          <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800/80 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/20 shrink-0">
              <CreditCard className="w-5 h-5" />
            </div>
            <div className="truncate">
              <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-white truncate">
                {editingTransaction ? 'Editar Movimiento' : (entryType === 'expense' ? 'Nueva Compra con Tarjeta' : 'Desglosar Saldo')}
              </h2>
              <p className="text-xs text-slate-500 truncate">
                {account?.name || 'Tarjeta de Crédito'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 flex items-center justify-center transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 overflow-y-auto overflow-x-hidden space-y-4 flex-1 w-full max-w-full">
          {/* SELECTOR: NUEVA COMPRA VS DESGLOSAR SALDO */}
          <div className="p-1.5 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200/60 dark:border-slate-700 space-y-1">
            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                onClick={() => setEntryType('expense')}
                className={`py-2.5 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                  entryType === 'expense'
                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm border border-indigo-200 dark:border-indigo-800'
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <CreditCard className="w-3.5 h-3.5" />
                Nueva Compra
              </button>
              <button
                type="button"
                onClick={() => setEntryType('breakdown')}
                className={`py-2.5 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                  entryType === 'breakdown'
                    ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 shadow-sm border border-purple-200 dark:border-purple-800'
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <PieChart className="w-3.5 h-3.5" />
                Desglosar Saldo
              </button>
            </div>
            <p className="text-[11px] text-center text-slate-500 dark:text-slate-400 pt-0.5">
              {entryType === 'expense' 
                ? '💳 Suma este gasto a la deuda de tu tarjeta y a gastos generales.'
                : '📊 Atribuye parte del saldo actual de la tarjeta sin alterar la deuda.'}
            </p>
          </div>

          {/* PASO 1: COMERCIO Y CONCEPTO */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1.5">
                <Store className="w-3.5 h-3.5 text-indigo-500" />
                Comercio / Tienda
              </label>
              <input
                type="text"
                placeholder={account?.credit_card_type === 'department' ? account?.name : 'Ej: Liverpool, Amazon, Costco'}
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-semibold text-slate-900 dark:text-white focus:bg-white focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-indigo-500" />
                Concepto / Qué compraste *
              </label>
              <input
                type="text"
                required
                placeholder="Ej: Pantalla 65 pulgadas, Ropa, Tenis"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-semibold text-slate-900 dark:text-white focus:bg-white focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
          </div>

          {/* PASO 2: PRECIO / MONTO */}
          <div className="text-center py-3.5 bg-slate-50/80 dark:bg-slate-800/40 rounded-3xl border border-slate-100 dark:border-slate-800">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-1">
              Precio / Monto de la Compra *
            </span>
            <div className="inline-flex items-center justify-center gap-1 w-full max-w-full">
              <span className="text-2xl sm:text-3xl font-black text-slate-400 shrink-0">$</span>
              <input
                type="text"
                inputMode="decimal"
                required
                placeholder="0.00"
                value={amount ? formatCurrencyInput(amount) : ''}
                onChange={(e) => setAmount(parseCurrency(e.target.value))}
                className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white bg-transparent border-none outline-none text-center w-full max-w-[14rem] sm:max-w-xs focus:ring-0"
              />
            </div>
          </div>

          {/* PASO 3: FECHA Y CATEGORÍA (Todas las categorías del sistema) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                Fecha de la Compra *
              </label>
              <input
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-semibold text-slate-900 dark:text-white"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  Categoría
                </label>
                <button
                  type="button"
                  onClick={() => setShowNewCatModal(true)}
                  className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-0.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Nueva
                </button>
              </div>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-semibold text-slate-900 dark:text-white"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Mini Modal para Crear Nueva Categoría */}
          {showNewCatModal && (
            <div className="p-3 bg-indigo-50/80 dark:bg-indigo-950/40 rounded-2xl border border-indigo-200 dark:border-indigo-800 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-indigo-950 dark:text-indigo-200 flex items-center gap-1">
                  <Tag className="w-3.5 h-3.5 text-indigo-600" />
                  Agregar Nueva Categoría
                </span>
                <button
                  type="button"
                  onClick={() => { setShowNewCatModal(false); setNewCatName(''); }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Nombre de la categoría (ej: Farmacia, Mascotas)"
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  className="flex-1 px-3 py-1.5 bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs font-semibold"
                />
                <button
                  type="button"
                  disabled={creatingCat}
                  onClick={handleCreateCategory}
                  className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all disabled:opacity-50"
                >
                  {creatingCat ? 'Creando...' : 'Guardar'}
                </button>
              </div>
            </div>
          )}

          {/* PASO 4: SELECTOR CLARO: ¿PERSONAL O DE ALGUIEN MÁS? */}
          <div className="pt-2">
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
              ¿A quién le corresponde este gasto?
            </label>
            <div className="grid grid-cols-2 gap-2 p-1.5 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200/60 dark:border-slate-700 w-full max-w-full">
              <button
                type="button"
                onClick={() => setOwnership('personal')}
                className={`py-3 px-2 sm:px-3 rounded-xl text-[11px] sm:text-xs font-black transition-all flex items-center justify-center gap-1.5 sm:gap-2 min-w-0 ${
                  ownership === 'personal'
                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-md border border-indigo-200 dark:border-indigo-800'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                }`}
              >
                <User className="w-4 h-4 shrink-0" />
                <span className="truncate">Compra Personal (Mía)</span>
              </button>
              <button
                type="button"
                onClick={() => setOwnership('shared')}
                className={`py-3 px-2 sm:px-3 rounded-xl text-[11px] sm:text-xs font-black transition-all flex items-center justify-center gap-1.5 sm:gap-2 min-w-0 ${
                  ownership === 'shared'
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                }`}
              >
                <UserCheck className="w-4 h-4 shrink-0" />
                <span className="truncate">Pagué por alguien más</span>
              </button>
            </div>
          </div>

          {/* CASO A: SI ES COMPRA PERSONAL (MÍA) */}
          {ownership === 'personal' && (
            <div className="p-4 bg-slate-50/80 dark:bg-slate-800/40 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3 w-full max-w-full overflow-x-hidden">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block">
                Modalidad de Pago en tu Tarjeta:
              </label>
              <div className="grid grid-cols-3 gap-1.5 w-full">
                <button
                  type="button"
                  onClick={() => setPersonalMode('normal')}
                  className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all truncate min-w-0 ${
                    personalMode === 'normal'
                      ? 'bg-rose-600 text-white shadow-sm'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  💳 Contado
                </button>
                <button
                  type="button"
                  onClick={() => setPersonalMode('msi')}
                  className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all flex items-center justify-center gap-1 truncate min-w-0 ${
                    personalMode === 'msi'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <CreditCard className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">A Meses (MSI)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPersonalMode('retention')}
                  className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all flex items-center justify-center gap-1 truncate min-w-0 ${
                    personalMode === 'retention'
                      ? 'bg-amber-600 text-white shadow-sm'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <Lock className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">Retención</span>
                </button>
              </div>

              {/* Sub-bloque Personal: A Meses (MSI) */}
              {personalMode === 'msi' && (
                <div className="pt-2 space-y-3 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-blue-900 dark:text-blue-300">
                      Plazo de MSI en Tarjeta:
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsCustomPersonalMsi(!isCustomPersonalMsi)}
                      className="text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {isCustomPersonalMsi ? 'Ver opciones comunes' : 'Otro plazo'}
                    </button>
                  </div>

                  {!isCustomPersonalMsi ? (
                    <div className="grid grid-cols-6 gap-1 sm:gap-1.5 w-full">
                      {QUICK_MSI_OPTIONS.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setPersonalMsiMonths(opt)}
                          className={`py-2 px-0.5 sm:px-1 rounded-xl text-[11px] sm:text-xs font-bold border transition-all min-w-0 text-center ${
                            personalMsiMonths === opt
                              ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                              : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {opt}m
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      type="number"
                      min="2"
                      max="72"
                      placeholder="Escribe el número de meses (ej: 18)"
                      value={customPersonalMsiMonths}
                      onChange={(e) => setCustomPersonalMsiMonths(e.target.value)}
                      className="w-full px-3.5 py-2 bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl text-xs font-bold"
                    />
                  )}

                  {/* Meses Ya Pagados Previamente */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center pt-1 border-t border-slate-200/60 dark:border-slate-700">
                    <div>
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block">
                        Meses ya pagados antes
                      </label>
                      <p className="text-[10px] text-slate-500">
                        Auto-calculado si la compra fue pasada.
                      </p>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max={activePersonalMsiMonths - 1}
                      value={personalMsiMonthsPaid}
                      onChange={(e) => setPersonalMsiMonthsPaid(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-full px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold"
                    />
                  </div>

                  {parsedAmount > 0 && (
                    <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-blue-100 dark:border-blue-900/40 text-xs space-y-1">
                      <div className="flex justify-between font-semibold">
                        <span className="text-slate-500">Pago mensual de tarjeta:</span>
                        <span className="font-bold text-blue-600">${personalMonthlyPayment.toFixed(2)}/mes</span>
                      </div>
                      <div className="flex justify-between font-semibold text-slate-600 dark:text-slate-300 pt-1 border-t border-slate-100 dark:border-slate-800">
                        <span>Saldo restante ({remainingPersonalMsiMonths} meses):</span>
                        <span className="font-black text-slate-900 dark:text-white">${remainingPersonalMsiBalance.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Sub-bloque Personal: Retención */}
              {personalMode === 'retention' && (
                <div className="pt-2 space-y-2.5 border-t border-slate-200 dark:border-slate-700">
                  <p className="text-[11px] text-amber-800 dark:text-amber-300">
                    La retención bloquea tu crédito temporalmente. Podrás liberarlo o cargarlo definitivamente después.
                  </p>
                  <div className="w-full max-w-full">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-amber-600" />
                      Fecha estimada de liberación
                    </label>
                    <div className="w-full max-w-full overflow-hidden rounded-xl">
                      <input
                        type="date"
                        value={personalRetentionDate}
                        onChange={(e) => setPersonalRetentionDate(e.target.value)}
                        className="w-full max-w-full box-border min-w-0 h-11 px-3.5 py-2 bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 rounded-xl text-xs sm:text-sm font-bold text-slate-900 dark:text-white text-left [&::-webkit-date-and-time-value]:text-left focus:ring-2 focus:ring-amber-500/20"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer pt-1">
                    <input
                      type="checkbox"
                      checked={personalRetentionAutoReminder}
                      onChange={(e) => setPersonalRetentionAutoReminder(e.target.checked)}
                      className="w-4 h-4 text-amber-600 rounded cursor-pointer"
                    />
                    <span>Crear recordatorio de verificación en Recordatorios de Pago</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* CASO B: SI SELECCIONAS "PAGUÉ POR ALGUIEN MÁS" */}
          {ownership === 'shared' && (
            <div className="p-4 bg-emerald-50/80 dark:bg-emerald-950/40 rounded-2xl border border-emerald-200 dark:border-emerald-900 space-y-3">
              {/* Nombre de la persona */}
              <div>
                <label className="text-xs font-bold text-emerald-950 dark:text-emerald-200 block mb-1">
                  ¿Quién te debe el dinero? *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Hermano, Juan, Carlos, Mamá"
                  value={sharedName}
                  onChange={(e) => setSharedName(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-800 border border-emerald-300 dark:border-emerald-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>

              {/* Correo de la persona (opcional) */}
              <div>
                <label className="text-xs font-bold text-emerald-950 dark:text-emerald-200 block mb-1">
                  Correo de quien te debe (opcional)
                </label>
                <input
                  type="email"
                  placeholder="correo@ejemplo.com (para enviar recordatorio si no tiene cuenta)"
                  value={sharedEmail}
                  onChange={(e) => setSharedEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-800 border border-emerald-300 dark:border-emerald-700 rounded-xl text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>

              {/* Plan de cobro para el deudor */}
              <div className="p-3 bg-white dark:bg-slate-850 rounded-xl border border-emerald-200 dark:border-emerald-800/80 space-y-2.5">
                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 block">
                  ¿Cómo te va a pagar {sharedName.trim() || 'esa persona'}?
                </span>
                <div className="grid grid-cols-3 gap-1.5 w-full">
                  <button
                    type="button"
                    onClick={() => setSharedPaymentMode('full')}
                    className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all truncate min-w-0 ${
                      sharedPaymentMode === 'full'
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    💵 Contado
                  </button>
                  <button
                    type="button"
                    onClick={() => setSharedPaymentMode('installments')}
                    className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all flex items-center justify-center gap-1 truncate min-w-0 ${
                      sharedPaymentMode === 'installments'
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    📅 <span className="truncate">Cuotas</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSharedPaymentMode('retention')}
                    className={`py-2 px-1 sm:px-2 rounded-xl text-[11px] sm:text-xs font-bold transition-all flex items-center justify-center gap-1 truncate min-w-0 ${
                      sharedPaymentMode === 'retention'
                        ? 'bg-amber-600 text-white shadow-sm'
                        : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <Lock className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">Retención</span>
                  </button>
                </div>

                {/* Si el deudor te pagará a Meses */}
                {sharedPaymentMode === 'installments' && (
                  <div className="pt-2 space-y-2.5 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                        Número de cuotas / meses:
                      </span>
                      <button
                        type="button"
                        onClick={() => setIsCustomSharedInstallments(!isCustomSharedInstallments)}
                        className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline"
                      >
                        {isCustomSharedInstallments ? 'Ver opciones comunes' : 'Otro plazo (Personalizado)'}
                      </button>
                    </div>

                    {!isCustomSharedInstallments ? (
                      <div className="grid grid-cols-6 gap-1 sm:gap-1.5 w-full">
                        {QUICK_MSI_OPTIONS.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setSharedInstallments(opt)}
                            className={`py-2 px-0.5 sm:px-1 rounded-xl text-[11px] sm:text-xs font-bold border transition-all min-w-0 text-center ${
                              sharedInstallments === opt
                                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                                : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            {opt}m
                          </button>
                        ))}
                      </div>
                    ) : (
                      <input
                        type="number"
                        min="2"
                        max="72"
                        placeholder="Escribe el número de meses (ej: 5, 8, 15)"
                        value={customSharedInstallments}
                        onChange={(e) => setCustomSharedInstallments(e.target.value)}
                        className="w-full px-3.5 py-2 bg-white dark:bg-slate-800 border border-emerald-300 dark:border-emerald-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white"
                      />
                    )}

                    {parsedAmount > 0 && (
                      <p className="text-[11px] text-emerald-700 dark:text-emerald-300 font-bold bg-emerald-50/70 dark:bg-emerald-950/40 p-2 rounded-lg">
                        ✓ {sharedName.trim() || 'El deudor'} pagará {activeSharedInstallments} cuotas de {formatCurrency(sharedMonthlyPayment)} al mes
                      </p>
                    )}
                  </div>
                )}

                {/* Si es Retención */}
                {sharedPaymentMode === 'retention' && (
                  <div className="pt-2 space-y-2 border-t border-slate-100 dark:border-slate-800 w-full max-w-full">
                    <div className="w-full max-w-full">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-amber-600" />
                        Fecha estimada de liberación de retención
                      </label>
                      <div className="w-full max-w-full overflow-hidden rounded-xl">
                        <input
                          type="date"
                          value={sharedRetentionDate}
                          onChange={(e) => setSharedRetentionDate(e.target.value)}
                          className="w-full max-w-full box-border min-w-0 h-11 px-3.5 py-2 bg-slate-50 dark:bg-slate-800 border border-amber-300 dark:border-amber-700 rounded-xl text-xs sm:text-sm font-bold text-slate-900 dark:text-white text-left [&::-webkit-date-and-time-value]:text-left focus:ring-2 focus:ring-amber-500/20"
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        checked={sharedRetentionAutoReminder}
                        onChange={(e) => setSharedRetentionAutoReminder(e.target.checked)}
                        className="w-4 h-4 text-amber-600 rounded cursor-pointer"
                      />
                      <span>Recordatorio de verificación de retención</span>
                    </label>
                  </div>
                )}
              </div>

              {/* Datos Bancarios SPEI para recibir la transferencia */}
              <div className="p-3 bg-white dark:bg-slate-850 rounded-xl border border-emerald-200 dark:border-emerald-800/80 space-y-2 w-full max-w-full">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 pb-1 border-b border-emerald-100 dark:border-emerald-900/60">
                  <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5 shrink-0">
                    <Building className="w-3.5 h-3.5 text-emerald-600" />
                    Datos para recibir transferencia (SPEI)
                  </span>
                  {bankAccounts.length > 0 && (
                    <select
                      onChange={(e) => {
                        const acc = bankAccounts.find(a => a.id === e.target.value);
                        if (acc) {
                          setBankName(acc.bank_name || acc.name);
                          if (acc.clabe) setBankClabe(acc.clabe);
                          if (acc.holder_name) setBankHolder(acc.holder_name);
                          else if (!bankHolder) setBankHolder(user?.name || '');
                        }
                      }}
                      className="text-[11px] text-indigo-600 dark:text-indigo-400 font-bold bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 cursor-pointer outline-none w-full sm:w-auto"
                    >
                      <option value="">⚡ Cargar de mis cuentas...</option>
                      {bankAccounts.map(b => (
                        <option key={b.id} value={b.id}>
                          {b.name}{b.clabe ? ' (✓ CLABE)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="space-y-2 pt-0.5">
                  <div>
                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">Titular</label>
                    <input
                      type="text"
                      placeholder="Nombre completo"
                      value={bankHolder}
                      onChange={(e) => setBankHolder(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-white"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">Banco</label>
                      <input
                        type="text"
                        placeholder="Ej: BBVA, Nu, Banorte"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">CLABE (18 dígitos)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={18}
                        placeholder="012345678901234567"
                        value={bankClabe}
                        onChange={(e) => setBankClabe(e.target.value.replace(/\D/g, ''))}
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-mono font-bold tracking-wider text-slate-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Fecha Límite Opcional y Recordatorio */}
              <div className="space-y-2">
                <div>
                  <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Fecha límite para que te pague (opcional)
                  </label>
                  <input
                    type="date"
                    value={sharedDueDate}
                    onChange={(e) => setSharedDueDate(e.target.value)}
                    className="w-full px-3.5 py-2 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs font-medium text-slate-900 dark:text-white"
                  />
                </div>
                {sharedDueDate && (
                  <label className="flex items-start gap-2 text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer pt-0.5 select-none">
                    <input
                      type="checkbox"
                      checked={sharedAutoReminder}
                      onChange={(e) => setSharedAutoReminder(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-emerald-600 rounded cursor-pointer focus:ring-emerald-500"
                    />
                    <div>
                      <span>Crear recordatorio de cobro en Recordatorios de Pago</span>
                      <p className="text-[10px] font-normal text-emerald-800 dark:text-emerald-300">
                        {sharedEmail
                          ? `Notificará a tu cuenta y enviará recordatorio al correo ${sharedEmail} si no tiene cuenta.`
                          : 'Te avisará antes de la fecha límite y se creará en su cuenta si vincula la deuda.'}
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* RESUMEN DE LA ACCIÓN */}
          {parsedAmount > 0 && (
            <div className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-200 dark:border-slate-700 text-xs space-y-1">
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>Cargo total a tu tarjeta:</span>
                <span className="font-black text-slate-900 dark:text-white">{formatCurrency(parsedAmount)}</span>
              </div>
              {ownership === 'shared' && (
                <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-bold pt-1 border-t border-slate-200 dark:border-slate-700">
                  <span>Se registrará cobro a {sharedName.trim() || 'esa persona'}:</span>
                  <span>+{formatCurrency(parsedAmount)}</span>
                </div>
              )}
            </div>
          )}

          {/* Botones Guardar / Cancelar */}
          <div className="pt-2 flex items-center justify-end gap-2 sm:gap-3 border-t border-slate-100 dark:border-slate-800 w-full">
            <button
              type="button"
              onClick={onClose}
              className="px-4 sm:px-5 py-3 rounded-2xl text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 sm:flex-initial px-5 sm:px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 active:scale-98 text-white rounded-2xl text-xs font-bold shadow-lg shadow-indigo-500/25 transition-all disabled:opacity-50 text-center"
            >
              {loading ? 'Guardando...' : (editingTransaction ? 'Guardar Cambios' : (entryType === 'expense' ? 'Registrar Compra' : 'Registrar Desglose'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreditCardBreakdownModal;
